import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildAuthRedirectState } from '../utils/authRedirect';
import { isVerifiedRoute } from './verifiedRoutes';

export function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null; // optionally a loader
  if (!user) {
    return <Navigate to="/auth/login" replace state={buildAuthRedirectState(location)} />;
  } else if (!user.emailVerified) {
    return <Navigate to="/auth/verify" replace state={buildAuthRedirectState(location)} />;
  }
  return children;
}

export function VerifiedRouteRedirect({ gatedPathname }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const shouldRedirect =
    !loading && Boolean(user) && !user.emailVerified && isVerifiedRoute(gatedPathname ?? location.pathname);

  // Let the route commit before sending an unverified user to the verification page.
  useEffect(() => {
    if (!shouldRedirect) return;
    navigate('/auth/verify', { replace: true, state: buildAuthRedirectState(location) });
  }, [location, navigate, shouldRedirect]);

  return null;
}

/** Blocks the guarded subtree from mounting; a sibling redirect is too late for queries. */
export function VerifiedRouteGate({ gatedPathname, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const shouldRedirect =
    !loading && Boolean(user) && !user.emailVerified && isVerifiedRoute(gatedPathname ?? location.pathname);

  if (shouldRedirect) return <Navigate to="/auth/verify" replace state={buildAuthRedirectState(location)} />;
  return children;
}

/** Route guard for admin-only pages. Redirects non-admins to the app home. */
export function RequireAdmin({ children }) {
  const { user, loading, profileLoading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading || profileLoading) return null;
  if (!user) {
    return <Navigate to="/auth/login" replace state={buildAuthRedirectState(location)} />;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
}

/**
 * Route guard for premium-only content.
 * Shows a fallback component when the user is not a premium subscriber.
 * If no fallback is provided, renders nothing.
 */
export function RequirePremium({ children, fallback }) {
  const { user, loading, isPremium } = useAuth();
  const location = useLocation();

  if (loading) return null;

  // Not logged in at all → send to login
  if (!user) {
    return <Navigate to="/auth/login" replace state={buildAuthRedirectState(location)} />;
  }

  // Logged in but email not verified → send to verify
  if (!user.emailVerified) {
    return <Navigate to="/auth/verify" replace state={buildAuthRedirectState(location)} />;
  }

  // Logged in but not premium → show fallback
  if (!isPremium) {
    return fallback || null;
  }

  return children;
}
